import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  MapPinIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

// ── Modal wrapper ─────────────────────────────────────────────────────────────
// Délègue au Modal partagé pour un habillage cohérent avec les autres modals ;
// le contenu de chaque phase fournit déjà son propre padding.
const ModalWrapper = ({ children, onClose, title, wide }) => (
  <Modal open onClose={onClose} title={title} size={wide ? 'lg' : 'md'} bodyClassName="pb-0">
    {children}
  </Modal>
);

// ── Main component ────────────────────────────────────────────────────────────
const ForceDeVenteValidationModal = ({ requestId, onClose, onSuccess, distributorPrechosen = false, circuitTerrain = null }) => {
  // phases: 'loading' | 'assign' | 'signatures' | 'validating'
  // ('signatures' = suivi terrain : scan de l'attribution signée par le bénéficiaire)
  const [phase, setPhase] = useState('loading');
  const { currentUser } = useAuth();
  // « prend en charge lui-même » : animateur ou chef de zone
  const isAnimateur = currentUser?.role === 'animateur' || currentUser?.role === 'chef_de_zone';

  // Assign phase state
  const [animateurs, setAnimateurs] = useState([]);
  const [distributeurs, setDistributeurs] = useState([]);
  const [zoneName, setZoneName] = useState('');
  const [selectedAnimateur, setSelectedAnimateur] = useState('');
  const [selectedDistributeur, setSelectedDistributeur] = useState('');
  const [comment, setComment] = useState('');

  // Signatures phase state
  const [sigStatus, setSigStatus] = useState(null);
  // Loading / action states
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ── Init: check if already assigned ────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const statusRes = await api.forceDeVente.getSignatureStatus(requestId);
        const status = statusRes.data?.data;

        if (status && status.status === 'processing') {
          // Already assigned — go straight to signatures view
          setSigStatus(status);
          setPhase('signatures');
        } else {
          // Load zone, animateurs, distributeurs
          try {
            const supplyRes = await api.marketingValidation.getRequestSupplySource(requestId);
            const supply = supplyRes.data?.data;
            if (supply?.zone) {
              setZoneName(supply.zone.name || '');
              if (supply.zone.id) {
                const zoneRes = await api.zones.getById(supply.zone.id);
                const zoneData = zoneRes.data?.data;
                setAnimateurs(zoneData?.animateurs || []);
                setDistributeurs(
                  (zoneData?.distributeurs || []).filter((d) => d.isActive !== false)
                );
              }
            }
          } catch (_) {
            // Zone data unavailable — still show assign phase (empty lists)
          }
          setPhase('assign');
        }
      } catch (err) {
        toast.error('Erreur lors du chargement');
        setPhase('assign');
      }
    };
    init();
  }, [requestId]);

  // ── Poll signature status every 15 s in signatures phase ──────────────────
  useEffect(() => {
    if (phase !== 'signatures') return;
    const interval = setInterval(async () => {
      try {
        const res = await api.forceDeVente.getSignatureStatus(requestId);
        if (res.data?.data) setSigStatus(res.data.data);
      } catch (_) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [phase, requestId]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAssign = async () => {
    if (!distributorPrechosen && (!selectedDistributeur || (!isAnimateur && !selectedAnimateur))) {
      toast.error(isAnimateur ? 'Sélectionnez un distributeur' : 'Sélectionnez un animateur et un distributeur');
      return;
    }
    setLoading(true);
    try {
      const res = await api.forceDeVente.assign(requestId, {
        ...(isAnimateur ? {} : { assignedAnimateurId: selectedAnimateur }),
        ...(distributorPrechosen ? {} : { distributeurId: selectedDistributeur }),
        comment,
      });
      const data = res.data?.data;
      const statusRes = await api.forceDeVente.getSignatureStatus(requestId);
      setSigStatus(statusRes.data?.data);

      toast.success(data?.message || 'Assignation effectuée avec succès');
      setPhase('signatures');
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'assignation");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setPhase('validating');
    try {
      await api.forceDeVente.validate(requestId);
      toast.success('Étape Force de Vente validée avec succès');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la validation');
      setPhase('signatures');
    }
  };

  // L'attribution est signée à la main par le bénéficiaire : on en téléverse le scan.
  const handleUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const upload = await api.forceDeVente.uploadSignedAttribution(requestId, formData);
      toast.success(upload.data?.data?.message || 'Attribution signée téléversée');
      // Demande terrain : le scan l'a fait passer à l'étape suivante.
      if (upload.data?.data?.currentStage && upload.data.data.currentStage !== 'force_de_vente') {
        onSuccess?.();
        onClose?.();
        return;
      }
      const res = await api.forceDeVente.getSignatureStatus(requestId);
      if (res.data?.data) setSigStatus(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      const res = await api.forceDeVente.getSignatureStatus(requestId);
      if (res.data?.data) setSigStatus(res.data.data);
      toast.success('Statut mis à jour');
    } catch (_) {}
  };

  // ── Loading phase ──────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <ModalWrapper onClose={onClose}>
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600" />
        </div>
      </ModalWrapper>
    );
  }

  // ── Assign phase ───────────────────────────────────────────────────────────
  if (phase === 'assign') {
    return (
      <ModalWrapper onClose={onClose} title="Force de Vente — Assignation">
        <div className="px-6 pb-4 space-y-4">
          {/* Zone badge */}
          {zoneName && (
            <div className="flex items-center gap-2 p-3 bg-primary-50/80 border border-primary-200 rounded-lg">
              <MapPinIcon className="h-4 w-4 text-primary-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-primary-500 uppercase tracking-wide">
                  Zone sélectionnée par le marketing
                </p>
                <p className="text-sm font-semibold text-primary-800">{zoneName}</p>
              </div>
            </div>
          )}

          {/* Animateur */}
          <div className="bg-white/50 rounded-xl p-4 border border-white/60 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <UserGroupIcon className="h-4 w-4 text-gray-500" />
              <label className="text-sm font-semibold text-gray-800">
                Animateur assigné <span className="text-red-500">*</span>
              </label>
            </div>
            {isAnimateur ? (
              <div className="flex items-center gap-2 p-3 bg-primary-50/80 border border-primary-200 rounded-lg text-sm text-primary-800">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-primary-600" />
                Vous prenez en charge cette demande&nbsp;: <span className="font-semibold">{currentUser?.name}</span>
              </div>
            ) : animateurs.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Aucun animateur lié à cette zone.
              </div>
            ) : (
              <select
                value={selectedAnimateur}
                onChange={(e) => setSelectedAnimateur(e.target.value)}
                className="form-input w-full rounded-lg border-gray-200 bg-white/80 text-sm focus:border-primary-400 focus:ring-primary-400 cursor-pointer"
              >
                <option value="">-- Sélectionner un animateur --</option>
                {animateurs.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Distributeur — masqué si déjà choisi à la création (cas court) */}
          {!distributorPrechosen && (
          <div className="bg-white/50 rounded-xl p-4 border border-white/60 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <BuildingStorefrontIcon className="h-4 w-4 text-gray-500" />
              <label className="text-sm font-semibold text-gray-800">
                Distributeur <span className="text-red-500">*</span>
              </label>
            </div>
            {distributeurs.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Aucun distributeur actif lié à cette zone.
              </div>
            ) : (
              <select
                value={selectedDistributeur}
                onChange={(e) => setSelectedDistributeur(e.target.value)}
                className="form-input w-full rounded-lg border-gray-200 bg-white/80 text-sm focus:border-primary-400 focus:ring-primary-400 cursor-pointer"
              >
                <option value="">-- Sélectionner un distributeur --</option>
                {distributeurs.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                    {d.code ? ` (${d.code})` : ''}
                    {d.city ? ` — ${d.city}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          )}

          {/* Comment */}
          <div>
            <label className="form-label">Commentaire (optionnel)</label>
            <textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="form-input w-full rounded-lg text-sm"
              placeholder="Notes sur cette assignation..."
            />
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 p-3 bg-blue-50/80 border border-blue-200 rounded-lg text-sm text-blue-700">
            <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
            Après assignation, la décharge est générée. Le bénéficiaire signe l'attribution sur papier
            et vous en téléversez le scan ; le distributeur signera la décharge après le chef de zone.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-row-reverse gap-3 rounded-b-xl">
          <button
            type="button"
            onClick={handleAssign}
            disabled={loading || (!distributorPrechosen && (!selectedDistributeur || (!isAnimateur && !selectedAnimateur)))}
            className="inline-flex justify-center items-center gap-2 rounded-lg border border-transparent px-5 py-2.5 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            Assigner la demande
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex justify-center rounded-lg border border-gray-300 px-5 py-2.5 bg-white/70 text-sm font-medium text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 cursor-pointer"
          >
            Annuler
          </button>
        </div>
      </ModalWrapper>
    );
  }

  // ── Suivi terrain ──────────────────────────────────────────────────────────
  // L'animateur valide seul : seul le scan de l'attribution conditionne sa validation.
  // Le distributeur signe la décharge après le chef de zone.
  const requiresBeneficiaire = sigStatus?.requiresBeneficiaire !== false;
  const scanReceived = !!sigStatus?.signedAttributionUrl;
  const canValidate = !requiresBeneficiaire || scanReceived;
  // Demande terrain : le téléversement du scan vaut validation, pas de bouton en plus.
  const scanValide = !!circuitTerrain && requiresBeneficiaire && !scanReceived;
  const suite = circuitTerrain === 'chef_de_zone' ? 'au distributeur' : 'au chef de zone';

  return (
    <ModalWrapper onClose={onClose} title="Force de Vente — Validation terrain" wide>
      <div className="px-6 pb-0">
        <div className="p-3 bg-primary-50/80 border border-primary-200 rounded-lg text-sm space-y-1 mb-4">
          {sigStatus?.dischargeNumber && (
            <p>
              <span className="text-primary-500">Décharge N° </span>
              <span className="font-semibold text-primary-800">{sigStatus.dischargeNumber}</span>
            </p>
          )}
          {sigStatus?.animatorName && (
            <p>
              <span className="text-primary-500">Animateur </span>
              <span className="font-medium text-primary-800">{sigStatus.animatorName}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 mb-5">
          {/* Attribution signée sur papier par le bénéficiaire */}
          {requiresBeneficiaire && (
            <div className={`border-2 rounded-xl p-4 ${scanReceived ? 'border-green-300 bg-green-50/80' : 'border-white/60 bg-white/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                {scanReceived
                  ? <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                  : <ClockIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                <div>
                  <p className="text-sm font-semibold text-gray-900">Attribution signée</p>
                  <p className="text-xs text-gray-500">Signée à la main par le bénéficiaire</p>
                </div>
              </div>
              {scanReceived ? (
                <a
                  href={`/${sigStatus.signedAttributionUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-green-700 font-medium hover:underline"
                >
                  Scan reçu — voir le fichier
                </a>
              ) : (
                <>
                  <p className="text-xs text-gray-600 mb-2">
                    Imprimez l'attribution, faites-la signer, puis téléversez le scan (PDF, JPG ou PNG).
                  </p>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    disabled={uploading}
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                  />
                  {uploading && <p className="mt-1 text-xs text-gray-500">Téléversement en cours...</p>}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 p-3 bg-blue-50/80 border border-blue-200 rounded-lg text-sm text-blue-700 mb-4">
          <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
          {scanValide
            ? `Le téléversement du scan transmet la demande ${suite}.`
            : 'Après votre validation, la demande part au chef de zone, puis au distributeur qui signe la décharge.'}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-b-xl">
        <button
          type="button"
          onClick={refreshStatus}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-150 cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Actualiser le statut
        </button>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex justify-center rounded-lg border border-gray-300 px-5 py-2.5 bg-white/70 text-sm font-medium text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 cursor-pointer"
          >
            Fermer
          </button>
          {!scanValide && (
          <button
            type="button"
            onClick={handleValidate}
            disabled={!canValidate || phase === 'validating'}
            className="inline-flex justify-center items-center gap-2 rounded-lg border border-transparent px-5 py-2.5 bg-green-600 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
          >
            {phase === 'validating' && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {canValidate ? "Valider l'étape FDV" : "En attente du scan de l'attribution"}
          </button>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default ForceDeVenteValidationModal;
